/**
 * SessionContext
 * Single source of truth for session data with flat maps, lazy loading, and real-time updates
 */

"use client";

import React from "react";
import {
  SessionDataContext,
  SessionLiveContext,
} from "./context";
import { useSessionProviderActionWiring } from "./use-session-provider-action-wiring";
import { useSessionProviderContextWiring } from "./use-session-provider-context-wiring";
import { useSessionProviderLiveWiring } from "./use-session-provider-live-wiring";
import { useSessionProviderOverviewWiring } from "./use-session-provider-overview-wiring";
import { useSessionProviderPlatform } from "./use-session-provider-platform";
import { useSessionProviderRuntimeWiring } from "./use-session-provider-runtime-wiring";
import { useSessionProviderStateBundle } from "./use-session-provider-state-bundle";

// ============================================================================
// Provider Props
// ============================================================================

interface SessionDataProviderProps {
  sessionId: string;
  children: React.ReactNode;
}

// ============================================================================
// Provider Implementation
// ============================================================================

export function SessionDataProvider({
  sessionId,
  children,
}: SessionDataProviderProps): React.JSX.Element {
  const platform = useSessionProviderPlatform({ sessionId });
  const state = useSessionProviderStateBundle();
  const liveWiring = useSessionProviderLiveWiring({
    userId: platform.userId,
    state,
  });
  const overviewWiring = useSessionProviderOverviewWiring({
    platform,
    state,
    liveWiring,
  });
  const runtimeWiring = useSessionProviderRuntimeWiring({
    platform,
    sessionId,
    state,
    liveWiring,
    overviewWiring,
  });
  const actionWiring = useSessionProviderActionWiring({
    platform,
    sessionId,
    state,
    liveWiring,
    overviewWiring,
    runtimeWiring,
  });
  const { value, liveValue } = useSessionProviderContextWiring({
    sessionId,
    state,
    liveWiring,
    overviewWiring,
    runtimeWiring,
    actionWiring,
  });

  return (
    <SessionDataContext.Provider value={value}>
      <SessionLiveContext.Provider value={liveValue}>
        {children}
      </SessionLiveContext.Provider>
    </SessionDataContext.Provider>
  );
}

export { useSessionDataContext, useSessionLiveContext } from "./context";
